import { useEffect, useMemo, useRef } from "react";
import { cn } from "../../utils";
import { PART_CLASSES } from "../dateTimePickerStyles";
import { useDigitSegment } from "../useDigitSegment";
import type { DateTimePickerClassNames } from "../types";
import { TimeColumn } from "./TimeColumn";

/** 24-hour time panel: two typed segments over two scrollable wheels. */

const HOUR_VALUES = Array.from({ length: 24 }, (_, i) => i);

const SEGMENT_CLASS =
  "w-6 shrink-0 cursor-default rounded bg-transparent text-center tabular-nums outline-none focus:bg-accent";

interface TimePanelProps {
  hours: number;
  minutes: number;
  onChange: (hours: number, minutes: number) => void;
  minuteStep?: number;
  classNames?: DateTimePickerClassNames;
  autoFocus?: boolean;
}

export function TimePanel({
  hours,
  minutes,
  onChange,
  minuteStep = 1,
  classNames,
  autoFocus = false,
}: TimePanelProps) {
  const hourRef = useRef<HTMLInputElement>(null);
  const minuteRef = useRef<HTMLInputElement>(null);

  const minuteValues = useMemo(() => {
    const step = Math.min(Math.max(1, Math.floor(minuteStep)), 60);
    return Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);
  }, [minuteStep]);

  const hourSegment = useDigitSegment({
    min: 0,
    max: 23,
    value: hours,
    onCommit: (next) => onChange(next, minutes),
    onAdvance: () => minuteRef.current?.focus(),
  });

  const minuteSegment = useDigitSegment({
    min: 0,
    max: 59,
    value: minutes,
    onCommit: (next) => onChange(hours, next),
  });

  // Taking DOM focus on open cannot be expressed in render.
  useEffect(() => {
    if (autoFocus) hourRef.current?.focus();
  }, [autoFocus]);

  return (
    <div data-part="time-panel" className="flex flex-col gap-3">
      <div className="flex items-center justify-center gap-1 rounded-lg border border-border bg-input-background px-3 py-2 text-sm text-foreground">
        <input
          ref={hourRef}
          type="text"
          inputMode="numeric"
          aria-label="Hour"
          value={hourSegment.text}
          onChange={() => {}}
          onKeyDown={hourSegment.handleKeyDown}
          onFocus={hourSegment.handleFocus}
          onBlur={hourSegment.handleBlur}
          className={SEGMENT_CLASS}
        />
        <span aria-hidden>:</span>
        <input
          ref={minuteRef}
          type="text"
          inputMode="numeric"
          aria-label="Minute"
          value={minuteSegment.text}
          onChange={() => {}}
          onKeyDown={minuteSegment.handleKeyDown}
          onFocus={minuteSegment.handleFocus}
          onBlur={minuteSegment.handleBlur}
          className={SEGMENT_CLASS}
        />
      </div>

      <div className={cn(PART_CLASSES.timePanel, classNames?.timePanel)}>
        <TimeColumn
          label="Hour"
          values={HOUR_VALUES}
          selected={hours}
          onSelect={(next) => onChange(next, minutes)}
          classNames={classNames}
        />
        <TimeColumn
          label="Minute"
          values={minuteValues}
          selected={minutes}
          onSelect={(next) => onChange(hours, next)}
          classNames={classNames}
        />
      </div>
    </div>
  );
}
